import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
  formatTooltipValueForSelectedMeasure,
} from '../ChartOptions/Format/NumberFormatter';
import { ChartsMapStateToProps, ChartsReduxStateProps } from './ChartsCommonInterfaces';

import { palette } from '../..';

import { limitChartData, shouldChartDisplayWithSecurity } from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const BoxPlotChart = ({
  // props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];

  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const { selectedMeasureName, tabState, selectedTab, showDash, chartAxes, chartType } =
    useSelector((state: RootState) => ({
      selectedMeasureName:
        state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
      tabState: state.tabState,
      selectedTab: state.tabTileProps.selectedTabId,
      showDash: state.tabTileProps.showDash,
      chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
      chartType: state.chartProperties?.properties[propKey]?.chartType,
    }));
  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [dimensionData, setDimensionData] = useState<string[]>([]);
  const [sourceData, setSourceData] = useState<any[]>([]);

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);

  // to track  the axis swap and assign axis name accordingly
  const axisName1: string = chartControl.boxPlotChartControls.flipAxis ? 'yAxis' : 'xAxis';
  const axisName2: string = !chartControl.boxPlotChartControls.flipAxis ? 'yAxis' : 'xAxis';
  var minimumValueOfYaxis: number;
  var maximumValueOfYaxis: number;

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 5000, chartAxes[3]?.fields[0]?.displayname);

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 5000 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 5000 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length >= 1) {
      // distribution value
      var dimValue: string = '';

      if (chartProperties.properties[propKey].chartAxes[1].fields.length > 0) {
        //if switched to boxplot from other charts without dimension value
        // if ("timeGrain" in chartProperties.properties[propKey].chartAxes[1].fields[0]) {
        // 	dimValue = `${chartProperties.properties[propKey].chartAxes[1].fields[0].timeGrain} of ${chartProperties.properties[propKey].chartAxes[1].fields[0].fieldname}`;
        // } else {
        // 	dimValue = `${chartProperties.properties[propKey].chartAxes[1].fields[0].fieldname}`;
        // }
        dimValue = chartProperties.properties[propKey].chartAxes[1].fields[0].displayname;
        var dimArray: string[] = limitedChartData.map((el: any) => {
          return el[dimValue];
        });

        setDimensionData([...new Set(dimArray)]);

        // var measureValue = `${chartProperties.properties[propKey].chartAxes[3].fields[0].agg} of ${chartProperties.properties[propKey].chartAxes[3].fields[0].fieldname}`;
        var measureValue = chartProperties.properties[propKey].chartAxes[3].fields[0]?.displayname;

        var allMeasureValue: number[] = [];
        allMeasureValue = limitedChartData.map((el) => {
          return el[measureValue];
        });

        minimumValueOfYaxis = Math.min(...allMeasureValue);
        maximumValueOfYaxis = Math.max(...allMeasureValue);

        var arrayPoints: any[] = [];

        // getting array points
        [...new Set(dimArray)].forEach((el: string) => {
          var temp: string[] = [];

          limitedChartData.forEach((elm: any) => {
            if (el === elm[dimValue]) {
              temp.push(elm[measureValue]);
            }
          });
          arrayPoints.push(temp);
        });

        setSourceData(arrayPoints);
      }
    }
  }, [chartData, chartControl]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
      // } else if (chartControl.legendOptions?.position?.top === "bottom") {
      //   top = "90%";
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = dimensionData.findIndex((item: any) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) => String(item) === String(hovered[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (params?.name && hoveredRef.current?.[chartDataKeys[0]] !== params.name) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [chartDataKeys[0]]: params.name };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.name);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const isTooltipEnabled =
    chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable;

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <ReactEcharts
        opts={{ renderer: 'svg' }}
        ref={chartRef}
        onChartReady={handleChartReady}
        onEvents={{
          legendselectchanged: onLegendSelectChanged,
        }}
        // theme={chartControl.colorScheme}
        style={{
          // padding: "5px",
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: selectedTheme?.colors || [],
          backgroundColor: selectedTheme?.background || [],
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},

            type: 'scroll',
            show:
              Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.common.h
                ? chartControl.legendOptions?.showLegend
                : false,
            itemHeight: chartControl.legendOptions?.symbolHeight,
            itemWidth: chartControl.legendOptions?.symbolWidth,
            itemGap: chartControl.legendOptions?.itemGap,

            left: chartControl.legendOptions?.position?.left,
            top:
              chartControl.legendOptions?.position?.top !== 'bottom'
                ? getTopMarginForLegend()
                : null,
            // top: chartControl.legendOptions?.position?.top,
            bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
            orient: chartControl.legendOptions?.orientation,
            selected: currentLegendSelected,
            textStyle: {
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },
          grid: {
            left:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.left + 5 + '%',
            right:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.right + '%',
            top:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'top'
                  ? chartControl.chartMargin.top + 15 + '%'
                  : chartControl.chartMargin.top + '%',
            bottom:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'bottom'
                  ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 40
                  : chartControl.chartMargin.bottom + '%',
          },

          ...(softUI
            ? {
                shadowBlur: 10, // Shadow blur size
                shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
                shadowOffsetX: 3, // Horizontal shadow offset
                shadowOffsetY: 3, // Vertical shadow offset
              }
            : {}),

          tooltip: {
            show: isTooltipEnabled,
            trigger: 'item',
            appendToBody: true,
            // just formating data to shown in tooltiop in required formate
            formatter: function (params: any) {
              if (params.seriesName === 'boxplot') {
                const tooltipFormats =
                  chartControls.properties[propKey]?.formatOptions?.tooltipFormats;
                //---- format for dimension ----- //
                const currentDimUid =
                  chartProperties.properties[propKey]?.chartAxes[1]?.fields[0].uId;
                const formattedParamsName = formatTooltipValueForSelectedMeasure(
                  currentDimUid,
                  tooltipFormats,
                  params.name.trim(),
                );
                //---- format for measure ----- //
                const measureAxis = chartProperties.properties[propKey]?.chartAxes.find(
                  (axis: any) => axis.name === 'Measure',
                );
                const currentMUid = measureAxis.fields[0].uId;

                return `
                <div style="font-family: ${fontFamily};">
                <div align="center" style="font-family: ${fontFamily};" >${formattedParamsName}<br/>${params.seriesName} <div/>
                <table>
								<th>
								<tr>
								<td align="left" style="font-family: ${fontFamily};" >min &nbsp</td>
								<td align="right" style="font-family: ${fontFamily};" >
                ${formatTooltipValueForSelectedMeasure(
                  currentMUid,
                  tooltipFormats,
                  params.value[1],
                )}
                </td>
								</tr>

								<tr>
								<td align="left" style="font-family: ${fontFamily};" >Q1 &nbsp</td>
								<td align="right" style="font-family: ${fontFamily};" >
                ${formatTooltipValueForSelectedMeasure(
                  currentMUid,
                  tooltipFormats,
                  params.value[2],
                )}
                </td>
								</tr>

								<tr>
								<td align="left" style="font-family: ${fontFamily};" >median &nbsp</td>
								<td align="right" style="font-family: ${fontFamily};" >
                ${formatTooltipValueForSelectedMeasure(
                  currentMUid,
                  tooltipFormats,
                  params.value[3],
                )}
                </td>
								</tr>

								<tr>
								<td align="left" style="font-family: ${fontFamily};" >Q2 &nbsp</td>
								<td align="right" style="font-family: ${fontFamily};" >
                ${formatTooltipValueForSelectedMeasure(
                  currentMUid,
                  tooltipFormats,
                  params.value[4],
                )}
                </td>
								</tr>

								<tr>
								<td align="left" style="font-family: ${fontFamily};" >max &nbsp</td>
								<td align="right" style="font-family: ${fontFamily};" >
                ${formatTooltipValueForSelectedMeasure(
                  currentMUid,
                  tooltipFormats,
                  params.value[5],
                )}
                </td>
								</tr>

								</th>
								 </table>
                 </div>`;
              } else {
                return `<div style="font-family: ${fontFamily};">
                          ${params.name} <br/> ${params.seriesName} <br/> ${params.value[1]}
                        </div>`;
              }
            },
          },

          dataset: [
            {
              source: sourceData,
            },
            {
              transform: {
                type: 'boxplot',
                //to  show dimension value as axes value
                config: {
                  itemNameFormatter: function (params: any) {
                    return dimensionData[params.value];
                  },
                },
                print: true,
              },
            },
            {
              fromDatasetIndex: 1,
              fromTransformResult: 1,
            },
          ],

          label: {
            show:
              Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                ? chartControl.labelOptions.showLabel
                : false,
            fontSize: chartControl.labelOptions.fontSize,
            fontFamily: fontFamily,
            color: chartControl.labelOptions.labelColor
              ? chartControl.labelOptions.labelColor
              : chartThemes[0]?.dark
                ? '#ffffff'
                : '#757474',
            backgroundColor: chartControl.labelOptions.labelBackground
              ? chartControl.labelOptions.labelBackgroundColor
              : 'transparent',
            borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
            padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
            formatter: (value: any) => {
              var formattedValue = value.value[1];

              formattedValue = formatChartLabelValueForSelectedMeasure(
                chartControls.properties[propKey],
                chartProperties.properties[propKey],
                formattedValue,
                chartProperties.properties[propKey].chartAxes[
                  chartProperties.properties[propKey].chartAxes.findIndex(
                    (item: any) => item.name === 'Measure',
                  )
                ]?.fields[0]?.displayname
                  ? chartProperties.properties[propKey].chartAxes[
                      chartProperties.properties[propKey].chartAxes.findIndex(
                        (item: any) => item.name === 'Measure',
                      )
                    ]?.fields[0]?.displayname
                  : '',
              );

              return formattedValue;
            },
          },

          [axisName1]: {
            type: 'category',
            splitLine: {
              show: chartControl.axisOptions?.xSplitLine,
              lineStyle: {
                color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
                width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
                type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
              },
            },
            position: chartControl.axisOptions.xAxis[1].position,

            axisLine: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              onZero:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? false
                  : chartControl.axisOptions.xAxis[1].onZero,
            },

            show: true,

            name:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                ? chartControl.axisOptions.xAxis[1].name
                : '',
            nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
            nameGap: chartControl.axisOptions.xAxis[1].nameGap,
            nameTextStyle: {
              fontSize: chartControl.axisOptions.xAxis[1].nameSize,
              color: chartControl.axisOptions.xAxis[1].nameColor,
            },

            axisTick: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              alignWithLabel: true,
              length:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickSizeTop
                  : chartControl.axisOptions.xAxis[1].tickSizeBottom,
            },
            axisLabel: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              fontSize: chartControl.axisOptions.xAxis[1].fontSize,
              rotate:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickRotationTop
                  : chartControl.axisOptions.xAxis[1].tickRotationBottom,
              margin:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                  : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
              formatter: (value: any) => {
                const dimensionName = chartDataKeys[0];
                const fieldInAxes = chartAxes
                  ?.flatMap((axis: any) => axis?.fields || [])
                  ?.find((f: any) => f.displayname === dimensionName);

                const measureUid = fieldInAxes?.uId;

                const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
                  measureUid
                ] as any;

                if (labelFormat?.finalDateFormat) {
                  return formatChartXAxisValue(chartControl, value, labelFormat);
                }

                return value;
              },
            },
          },
          [axisName2]: {
            type: 'value',
            splitLine: {
              show: chartControl.axisOptions?.ySplitLine,
              lineStyle: {
                color: chartControl.axisOptions.yAxis[1]?.splitLineColor || '#d3d3d3',
                width: chartControl.axisOptions.yAxis[1]?.splitLineWidth ?? 1,
                type: chartControl.axisOptions.yAxis[1]?.splitLineType || 'solid',
              },
            },
            axisLine: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              onZero: chartControl.axisOptions.yAxis[1].onZero,
            },
            // min: chartControl.axisOptions.axisMinMax.enableMin
            //   ? chartControl.axisOptions.axisMinMax.minValue
            //   : minimumValueOfYaxis,
            // max: chartControl.axisOptions.axisMinMax.enableMax
            //   ? chartControl.axisOptions.axisMinMax.maxValue
            //   : maximumValueOfYaxis,
            inverse: chartControl.axisOptions.inverse,
            position: chartControl.axisOptions.yAxis[1].position,
            show: true,

            name:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                ? chartControl.axisOptions.yAxis[1].name
                : '',
            nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
            nameGap: chartControl.axisOptions.yAxis[1].nameGap,
            nameTextStyle: {
              fontSize: chartControl.axisOptions.yAxis[1].nameSize,
              color: chartControl.axisOptions.yAxis[1].nameColor,
            },
            axisTick: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              alignWithLabel: true,
              length:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickSizeLeft
                  : chartControl.axisOptions.yAxis[1].tickSizeRight,
            },
            axisLabel: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              fontSize: chartControl.axisOptions.yAxis[1].fontSize,
              rotate:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickRotationLeft
                  : chartControl.axisOptions.yAxis[1].tickRotationRight,
              margin:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
                  : chartControl.axisOptions.yAxis[1].tickPaddingRight,
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
              formatter: (value: number) => {
                var formattedValue = formatChartYAxisValue(chartControl, value);
                return formattedValue;
              },
            },
          },
          dataZoom: [
            {
              show: false,
              type: 'slider',
              start: 0,
              end: 100,
              xAxisIndex: [0],
              top: '90%',
            },
          ],
          series: [
            {
              name: 'boxplot',
              type: 'boxplot',
              datasetIndex: 1,
              colorBy: chartControl.boxPlotChartControls.colorBy,
              boxWidth: [
                chartControl.boxPlotChartControls.minBoxWidth,
                chartControl.boxPlotChartControls.maxBoxWidth,
              ],
              emphasis: {
                focus: 'self',
              },
              blur: {
                itemStyle: {
                  opacity: 0.5,
                },
              },
              itemStyle: softUI
                ? {
                    borderWidth: chartControl.boxPlotChartControls.boxborderWidth,
                    shadowBlur: 10,
                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                    shadowOffsetX: 3,
                    shadowOffsetY: 3,
                  }
                : {
                    borderWidth: chartControl.boxPlotChartControls.boxborderWidth,
                  },
            },
            {
              name: 'outlier',
              type: 'scatter',
              datasetIndex: 2,
            },
          ],
        }}
      />
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(BoxPlotChart);
